<script>
    import Dropzone from "svelte-file-dropzone";
    import {createEventDispatcher} from "svelte";
    import imageCompression from "browser-image-compression";
    import Service from "../utils/Service";

    export let model_root;
    export let content_id;
    export let file_type;

    const service = new Service(model_root);

    $ : uploadSizeLimit = (file_type === "image" ? 1000000 : 5000000);

    let uploadComponentVisible = false;
    let upload_finished = false;
    let uploading_files = false;

    const dispatch = createEventDispatcher();

    // files that have been dragged and dropped
    let files = {
        accepted: [],
        rejected: []
    };

    function handleFilesSelect(e) {
        const {acceptedFiles, fileRejections} = e.detail;
        files.accepted = [...files.accepted, ...acceptedFiles];
        files.rejected = [...files.rejected, ...fileRejections];
        files.processingInformation = Array.from({length: files.accepted.length}, () => {
            return {
                state: undefined,
                upload_success: undefined,
                upload_error: undefined,
                upload_error_type: undefined,
                upload_error_msg: undefined
            }
        })
    }

    // function to upload multiple files with detailed error handling
    async function uploadFiles() {
        uploading_files = true;
        let answersFromServer = 0;
        const fileCompressionOptions = {
            maxSizeMB: 1,
            maxWidthOrHeight: 1920,
            useWebWorker: true
        }
        for (let i = 0; i < files.accepted.length; i++) {
            let file = files.accepted[i];
            const originalName = file.name;

            if (file_type === "image" && file.type.split("/")[0] === "image") {
                files.processingInformation[i].state = "compressing";
                imageCompression(file, fileCompressionOptions).then(file => {
                    files.processingInformation[i].state = "compressed";
                    prepareForUpload(i, file, originalName).then(() => {
                        answersFromServer = checkAndUpdateUploadStatus(answersFromServer);
                    });
                });
            } else {
                prepareForUpload(i, file, originalName).then(() => {
                    answersFromServer = checkAndUpdateUploadStatus(answersFromServer);
                });
            }
        }
    }

    function checkAndUpdateUploadStatus(answersFromServer) {
        answersFromServer++;
        if (answersFromServer === files.accepted.length) {
            dispatch('upload_finished');
            upload_finished = true;
            uploading_files = false;
        }
        return answersFromServer;
    }

    async function prepareForUpload(index, file, originalName) {
        if (file_type === "image" && file.type.split("/")[0] !== "image") {
            throwError(index, "wrong_format", "This file is not an image.");
            return;
        }
        if (file.size > uploadSizeLimit) {
            throwError(index, "size_limit", "This file is too large. Upload limit is: " + (uploadSizeLimit / 1024 / 1024) + " MB");
        } else {
            files.processingInformation[index].state = "uploading";
            await service.uploadFile(content_id, originalName, file, file_type).then(data => {
                if (!data.success) {
                    throwError(index, "upload_error", data.msg);
                } else {
                    files.processingInformation[index].state = "uploaded";
                    files.processingInformation[index].upload_success = true;
                }
            })
        }
    }

    // method to show the upload menu
    export function showUploadMenu() {
        // scroll to top since there the upload menu is shown
        window.scrollTo(0, 0);
        if (uploadComponentVisible === false) {
            uploadComponentVisible = true;
            upload_finished = false;
        } else {
            // if the upload menu is already open highlight the close button in case the user wanted to close the menu and not open it a second time (why should he want to to that?)
            let closeButtons = document.getElementsByClassName("closeUploadComponent");
            for (let btn of closeButtons) {
                btn.classList.add("highlighted", "shadow");
                setTimeout(() => {
                    btn.classList.remove("highlighted", "shadow");
                }, 600)
            }
        }
    }

    // method to reset the drag n drop array
    function resetUploadArray() {
        files.accepted = [];
        files.rejected = [];
    }

    function throwError(fileIndex, errorType, errorMsg) {
        let file = files.accepted[fileIndex];
        files.processingInformation[fileIndex]["upload_success"] = false;
        files.processingInformation[fileIndex]["upload_error_type"] = errorType;
        files.processingInformation[fileIndex]["upload_error_msg"] = errorMsg;
    }

    // method to get the file icon by a given type
    let icon = function getFileIcon(type) {
        let mediaType = type.split("/")[0];
        let mediaSubType = type.split("/")[1];

        let iconClass = "far fa-file-alt";
        switch (mediaType) {
            case "application":
                switch (mediaSubType) {
                    case "pdf":
                        iconClass = "far fa-file-pdf";
                        break;
                }
                break;
            case "image":
                iconClass = "far fa-file-image";
                break;
        }
        return iconClass;
    }

    // method to remove a single item from the upload list
    function removeItemFromUploadlist(item) {
        files.accepted = files.accepted.filter(f => f.name !== item.name);
    }

    // cancel uploading and reset everything
    function cancelUploading() {
        if (upload_finished === false && files.accepted.length > 0) {
            bootbox.confirm({
                size: "small",
                message: "Are you sure? Otherwise, the selected files will no longer be available.",
                callback: async function (result) {
                    if (result === true) {
                        resetUploadMenu();
                    }
                }
            })
        } else {
            resetUploadMenu();
        }
    }

    function resetUploadMenu() {
        resetUploadArray();
        uploadComponentVisible = false;
    }
</script>

{#if uploadComponentVisible}

    <div class="mb-2 p-3 shadow">

        {#if files.accepted.length === 0}
            <div class="mb-3">
                <Dropzone on:drop={handleFilesSelect}>
                    <div id="dropZone">Drag and drop files here, or click to select files.</div>
                </Dropzone>
            </div>
        {/if}

        {#if files.accepted.length > 0}
            <ul class="list-group mb-4">
                {#each files.accepted as item, itemIndex}
                    <li class="list-group-item"
                        class:text-danger="{files.processingInformation[itemIndex].upload_success === false && files.processingInformation[itemIndex].upload_error_type !== 'file_exists'}"
                        class:text-warning="{files.processingInformation[itemIndex].upload_error_type === 'file_exists'}"
                        class:text-success="{files.processingInformation[itemIndex].upload_success}">
                        <div class="d-flex align-items-center">
                            <i class="{icon(item.type)} pe-2"></i>
                            {item.name}
                            {#if upload_finished === false}
                                <button class="btn btn-outline-dark ms-auto"
                                        on:click={() => {removeItemFromUploadlist(item)}}>
                                    <i class="far fa-trash-alt"></i>
                                    Unselect
                                </button>
                            {/if}
                        </div>
                        {#if files.processingInformation[itemIndex].upload_success !== true && files.processingInformation[itemIndex].upload_success !== false}
                            {#if files.processingInformation[itemIndex].state === "compressing"}
                                <p><i class="fas fa-circle-notch fa-spin me-2"></i>Compressing file</p>
                            {/if}
                            {#if files.processingInformation[itemIndex].state === "uploading"}
                                <p><i class="fas fa-circle-notch fa-spin me-2"></i>Uploading file</p>
                            {/if}
                        {/if}
                        {#if files.processingInformation[itemIndex].upload_success === false}
                            <div class="mb-1 mt-3 p-2 w-100 border rounded d-flex align-items-center"
                                 class:border-danger="{!files.processingInformation[itemIndex].upload_success && files.processingInformation[itemIndex].upload_error_type !== 'file_exists'}"
                                 class:border-warning="{files.processingInformation[itemIndex].upload_error_type === 'file_exists'}">
                                <i
                                        class:fa-ban="{!files.processingInformation[itemIndex].upload_success && files.processingInformation[itemIndex].upload_error_type !== 'file_exists'}"
                                        class:fa-copy="{files.processingInformation[itemIndex].upload_error_type === 'file_exists'}"
                                        class="fas me-2">
                                </i>
                                {files.processingInformation[itemIndex].upload_error_msg}
                            </div>
                        {/if}
                    </li>
                {/each}
                {#if !upload_finished && !uploading_files}
                    <li class="list-group-item d-flex align-items-center">
                        <Dropzone on:drop={handleFilesSelect}>
                            Append new files by drag and drop, or click to select.
                        </Dropzone>
                    </li>
                {/if}
            </ul>

            {#if !uploading_files && !upload_finished}
                <p><b>Almost done!</b> Click the "Upload files"-Button to upload your selected files.</p>
            {/if}

            {#if upload_finished}
                <p><b>Done!</b> If some files in the list appear red, an error occurred during the upload.</p>
            {/if}

            {#if uploading_files}
                <p><i class="fas fa-circle-notch fa-spin me-2"></i>Uploading files...</p>
            {/if}

            {#if !upload_finished && !uploading_files}
                <button class="btn btn-primary" on:click={uploadFiles}><i class="fas fa-upload me-1"></i>Upload files
                </button>
                <button class="btn btn-secondary" on:click={resetUploadArray}><i class="far fa-trash-alt me-1"></i>Unselect
                    all
                </button>
            {/if}

        {/if}

        {#if !uploading_files}
            {#if upload_finished}
                <button class="btn btn-primary" on:click={cancelUploading}><i class="fas fa-times-circle me-1"></i>Close
                </button>
            {:else}
                <button class="btn btn-secondary" on:click={cancelUploading}><i class="fas fa-times-circle me-1"></i>Cancel
                </button>
            {/if}
        {/if}

    </div>

{/if}

<style>
    .text-warning {
        color: #f15a22 !important;
    }

    .border-warning {
        border-color: #f15a22 !important;
    }

    #dropZone {
        width: 100%;
        height: 120px;
        padding: 45px;
        text-align: center;
    }

    :global(.closeUploadComponent.highlighted) {
        background-color: #19b5fe;
        border-color: #19b5fe;
    }
</style>
